import BigNumber from 'bignumber.js';
import {MAX_DECIMALS_IN_CRYPTO} from '~/config';
import {CryptoCurrencyTrait} from '~/entities/currencies';
import {BlockchainInfo} from '~/entities/operations-crypto';
import {decodeOrThrow} from '~/library/io-ts-tools';
import {AccountId, BaseCurrencyCode, CryptoAddress, Source} from '~/library/types';
import {Assets, Crypto, Fiat, Token} from '~/shared/api/legacy_services/Assets/Assets';

export type AccountBase = {
    id: AccountId;
    name: string;
    code: BaseCurrencyCode;
    balance: string;
    icon: Source | null;
};

export type FiatAccount = AccountBase & {
    type: 'fiat';
    decimals: number;
};

export type BlockchainAccountType = 'crypto' | 'stablecoin';

export type Blockchain = BlockchainInfo &
    AccountBase &
    CryptoCurrencyTrait & {
        balance: BigNumber;
    };

export type BlockchainAccount = AccountBase & {
    type: BlockchainAccountType;
    decimals: number;
    address: CryptoAddress;
    blockchain: Blockchain;
};

function mapAccountBase(account: Crypto | Token | Fiat): AccountBase {
    return {
        id: account.id,
        name: account.name,
        code: account.currency,
        balance: account.balance,
        icon: account.icon,
    } satisfies AccountBase;
}

export const selectAccountsFromAssets = (assets: Assets) => {
    const {crypto, tokens, fiat} = assets.accounts;

    const fiatAccounts = fiat.map(a => ({
        ...mapAccountBase(a),
        type: 'fiat',
        decimals: a.decimals,
    })) satisfies FiatAccount[];

    const blockchainAccounts = (
        [
            {type: 'crypto', accounts: crypto},
            {type: 'stablecoin', accounts: tokens},
        ] as const
    )
        .flatMap(({type, accounts}) =>
            accounts.map((account): BlockchainAccount | null => {
                const blockchainAccount =
                    account.blockchain.currency === account.currency
                        ? account
                        : crypto.find(c => c.currency === account.blockchain.currency);

                if (!blockchainAccount) {
                    return null;
                }

                const blockchain = {
                    id: blockchainAccount.id,
                    name: blockchainAccount.name,
                    icon: blockchainAccount.icon,
                    currencyCode: blockchainAccount.currency,
                    currencyDecimals: Math.min(blockchainAccount.decimals, MAX_DECIMALS_IN_CRYPTO),
                    networkName: blockchainAccount.blockchain.network_name,
                    balance: BigNumber(blockchainAccount.balance),
                } as Blockchain;
                return {
                    ...mapAccountBase(account),
                    type: type,
                    decimals: Math.min(account.decimals, MAX_DECIMALS_IN_CRYPTO),
                    address: decodeOrThrow(CryptoAddress, account.address),
                    blockchain: blockchain,
                };
            })
        )
        .filter(account => !!account);

    return {fiatAccounts, blockchainAccounts};
};
